import React, { createContext, useContext, useEffect, useState } from 'react';

export enum AppVersion {
  GENERIC_TAS = 'generic_tas',    // No branding, steps 1-3
  ROOSTER_TAS = 'rooster_tas',    // Rooster branded, steps 1-3
  ROI = 'roi',                     // Rooster branded, steps 1-4, no advanced
  ADMIN = 'admin'                  // Full features
}

interface VersionContextType {
  version: AppVersion;
  isRoosterBranded: boolean;
  hasROIAnalysis: boolean;
  hasAdvancedSettings: boolean;
  maxStep: number;
}

const VersionContext = createContext<VersionContextType | undefined>(undefined);

export const useVersion = () => {
  const context = useContext(VersionContext);
  if (!context) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  return context;
};

interface VersionProviderProps {
  children: React.ReactNode;
}

export const VersionProvider: React.FC<VersionProviderProps> = ({ children }) => {
  const [version, setVersion] = useState<AppVersion>(AppVersion.ROI);

  useEffect(() => {
    // Determine version based on URL path
    const path = window.location.pathname.toLowerCase();
    
    if (path.includes('/tas') || path.includes('/generic')) {
      setVersion(AppVersion.GENERIC_TAS);
    } else if (path.includes('/rooster-tas')) {
      setVersion(AppVersion.ROOSTER_TAS);
    } else if (path.includes('/admin')) {
      setVersion(AppVersion.ADMIN);
    } else {
      // Default to ROI calculator for root or /roi
      setVersion(AppVersion.ROI);
    }
  }, []);

  const contextValue: VersionContextType = {
    version,
    isRoosterBranded: version !== AppVersion.GENERIC_TAS,
    hasROIAnalysis: version === AppVersion.ROI || version === AppVersion.ADMIN,
    hasAdvancedSettings: version === AppVersion.ADMIN,
    maxStep: (version === AppVersion.GENERIC_TAS || version === AppVersion.ROOSTER_TAS) ? 3 : 4
  };

  return (
    <VersionContext.Provider value={contextValue}>
      {children}
    </VersionContext.Provider>
  );
};